import { useState } from 'react';
import { Box, Flex, Input, Button, Text } from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-toastify';
import { usePostHog } from '@posthog/react';

interface SignupFormProps {
  onSuccess?: (email: string) => void;
  onSwitchToLogin?: () => void;
}

const SignupForm = ({ onSuccess, onSwitchToLogin }: SignupFormProps) => {
  const { t } = useTranslation();
  const { signup } = useAuth();
  const posthog = usePostHog();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(t('auth.firstNameRequired')),
    lastName: Yup.string().required(t('auth.lastNameRequired')),
    email: Yup.string().required(t('auth.emailRequired')).email(t('auth.emailInvalid')),
    password: Yup.string()
      .required(t('auth.passwordRequired'))
      .min(8, t('auth.passwordMinLength')),
    confirmPassword: Yup.string()
      .required(t('auth.confirmPasswordRequired'))
      .oneOf([Yup.ref('password')], t('auth.passwordsMustMatch')),
    acceptTerms: Yup.boolean()
      .oneOf([true], t('auth.mustAcceptTerms'))
      .required(t('auth.mustAcceptTerms')),
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);

      try {
        await signup({
          first_name: values.firstName,
          last_name: values.lastName,
          email: values.email,
          password: values.password,
        });
        posthog?.identify(values.email, {
          email: values.email,
          first_name: values.firstName,
          last_name: values.lastName,
        });
        posthog?.capture('user_signed_up', { method: 'email' });
        toast.success(t('auth.signupSuccess'));
        onSuccess?.(values.email);
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail || t('auth.signupFailed');
        setError(errorMessage);
        toast.error(errorMessage);
        posthog?.captureException(err);
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <Box as="form" onSubmit={formik.handleSubmit as any}>
      <Flex direction="column" rowGap="16px">
        {/* Title */}
        <Flex justifyContent="center">
          <Box
            fontSize="26px"
            fontWeight="500"
            color="neutral.100"
          >
            {t('auth.signup')}
          </Box>
        </Flex>

        {/* First Name Field */}
        <Flex direction="column" rowGap="8px">
          <Flex justifyContent="space-between">
            <Box fontWeight="400" fontSize="14px" lineHeight="150%" color="neutral.300">
              {t('auth.firstName')}
            </Box>
            <Box textAlign="center" color="red" fontSize="14px" fontWeight="300">
              {formik.touched.firstName && formik.errors.firstName ? formik.errors.firstName : null}
            </Box>
          </Flex>
          <Input
            name="firstName"
            p="10px"
            fontWeight="400"
            fontSize="14px"
            lineHeight="150%"
            placeholder={t('auth.enterFirstName')}
            color="neutral.300"
            borderWidth="1px"
            borderColor="neutral.700"
            backgroundColor="neutral.800"
            borderRadius="8px"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Flex>

        {/* Last Name Field */}
        <Flex direction="column" rowGap="8px">
          <Flex justifyContent="space-between">
            <Box fontWeight="400" fontSize="14px" lineHeight="150%" color="neutral.300">
              {t('auth.lastName')}
            </Box>
            <Box textAlign="center" color="red" fontSize="14px" fontWeight="300">
              {formik.touched.lastName && formik.errors.lastName ? formik.errors.lastName : null}
            </Box>
          </Flex>
          <Input
            name="lastName"
            p="10px"
            fontWeight="400"
            fontSize="14px"
            lineHeight="150%"
            placeholder={t('auth.enterLastName')}
            color="neutral.300"
            borderWidth="1px"
            borderColor="neutral.700"
            backgroundColor="neutral.800"
            borderRadius="8px"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Flex>

        {/* Email Field */}
        <Flex direction="column" rowGap="8px">
          <Flex justifyContent="space-between">
            <Box fontWeight="400" fontSize="14px" lineHeight="150%" color="neutral.300">
              {t('auth.email')}
            </Box>
            <Box textAlign="center" color="red" fontSize="14px" fontWeight="300">
              {formik.touched.email && formik.errors.email ? formik.errors.email : null}
            </Box>
          </Flex>
          <Input
            name="email"
            type="email"
            p="10px"
            fontWeight="400"
            fontSize="14px"
            lineHeight="150%"
            placeholder={t('auth.enterEmail')}
            color="neutral.300"
            borderWidth="1px"
            borderColor="neutral.700"
            backgroundColor="neutral.800"
            borderRadius="8px"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Flex>

        {/* Password Field */}
        <Flex direction="column" rowGap="8px">
          <Flex justifyContent="space-between">
            <Box fontWeight="400" fontSize="14px" lineHeight="150%" color="neutral.300">
              {t('auth.password')}
            </Box>
            <Box textAlign="center" color="red" fontSize="14px" fontWeight="300">
              {formik.touched.password && formik.errors.password ? formik.errors.password : null}
            </Box>
          </Flex>
          <Input
            type="password"
            name="password"
            p="10px"
            fontWeight="400"
            fontSize="14px"
            lineHeight="150%"
            placeholder={t('auth.enterPassword')}
            color="neutral.300"
            borderWidth="1px"
            borderColor="neutral.700"
            backgroundColor="neutral.800"
            borderRadius="8px"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Flex>

        {/* Confirm Password Field */}
        <Flex direction="column" rowGap="8px">
          <Flex justifyContent="space-between">
            <Box fontWeight="400" fontSize="14px" lineHeight="150%" color="neutral.300">
              {t('auth.confirmPassword')}
            </Box>
            <Box textAlign="center" color="red" fontSize="14px" fontWeight="300">
              {formik.touched.confirmPassword && formik.errors.confirmPassword ? formik.errors.confirmPassword : null}
            </Box>
          </Flex>
          <Input
            type="password"
            name="confirmPassword"
            p="10px"
            fontWeight="400"
            fontSize="14px"
            lineHeight="150%"
            placeholder={t('auth.confirmYourPassword')}
            color="neutral.300"
            borderWidth="1px"
            borderColor="neutral.700"
            backgroundColor="neutral.800"
            borderRadius="8px"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Flex>

        {/* Terms Agreement */}
            <Flex
              as="label"
              gap="12px"
              cursor="pointer"
              alignItems="flex-start"
              py="4px"
            >
              <input
                type="checkbox"
                name="acceptTerms"
                checked={formik.values.acceptTerms}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                style={{
                  flexShrink: 0,
                  marginTop: 3,
                  width: 16,
                  height: 16,
                  accentColor: '#EFF2F6',
                  cursor: 'pointer',
                }}
              />
              <Box
                fontSize="13px"
                fontWeight="400"
                lineHeight="150%"
                color={formik.touched.acceptTerms && formik.errors.acceptTerms ? 'red' : 'neutral.400'}
              >
                I agree to the{' '}
                <a href="https://tryloyaltycards.com/terms" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', fontWeight: 500, color: 'inherit' }}>Terms of Service</a>
                {' '}and{' '}
                <a href="https://tryloyaltycards.com/privacy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', fontWeight: 500, color: 'inherit' }}>Privacy Policy</a>.
                {' '}I confirm that all customer data provided is collected with proper consent for transactional messaging.
              </Box>
            </Flex>

        {/* Submit Button */}
        <Flex alignItems="center" mt="8px">
          <Button
            type="submit"
            height="fit-content"
            width="100%"
            py="8px"
            fontWeight="500"
            fontSize="14px"
            color="neutral.900"
            backgroundColor="neutral.100"
            loading={isLoading}
            disabled={isLoading || !formik.isValid || !formik.dirty}
          >
            {t('auth.signup')}
          </Button>
        </Flex>

        {/* Error Message */}
        {error && (
          <Box mt="8px" textAlign="center" color="red" fontSize="14px" fontWeight="400">
            {error}
          </Box>
        )}

        {/* Switch to Login */}
        {onSwitchToLogin && (
          <Flex justifyContent="center" alignItems="center" columnGap="4px">
            <Box fontSize="14px" fontWeight="400" color="neutral.400">
              {t('auth.haveAccount')}
            </Box>
            <Text
              as="button"
              fontSize="14px"
              fontWeight="500"
              color="neutral.200"
              textDecoration="underline"
              cursor="pointer"
              bg="none"
              border="none"
              onClick={onSwitchToLogin}
              _hover={{ color: 'neutral.100' }}
            >
              {t('auth.logIn')}
            </Text>
          </Flex>
        )}
      </Flex>
    </Box>
  );
};

export default SignupForm;
